import { FC, useState } from 'react'
import { LatestContractDtoFragment, PublicProfileFragment, Employee, RoleName } from '../../../types'
import { EmployeeInformationComponent } from '../personal/EmployeeInformationComponent'
import { EducationTable } from '../personal/EducationTable'
import { ContractComponent } from '../contract/ContractComponent'
import { Divider, Col, Card, Switch, Tooltip } from 'antd'
import { SalaryHistoryTable } from '../contract/SalaryHistoryTable'
import { BonusHistoryTable } from '../contract/BonusHistoryTable'
import { NationalContractLevelHistoryTable } from '../contract/NationalContractLevelHistoryTable'
import { LevelHistoryTable } from '../contract/LevelHistoryTable'
import { PaymentHistoryTable } from '../contract/PaymentHistoryTable'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import useTagHistoryOfEmployeeUntilReplyEntryDate from '../../../graphql/ranking/hooks/useTagHistoryOfEmployeeUntilReplyEntryDate'
import userCurrentContractWithCareerPathEvaluationInProfile from '../../../graphql/contract/hooks/userCurrentContractWithCareerPathEvaluationInProfile'
import PerText from '../../../components/custom_components/Text'
import { useTranslation, Trans } from 'react-i18next'
import { getEmployeeFirstName } from '../../../shared/employeeUtil'
import { useAuth } from '../../../context/auth-context'
import { useUserManagersRolesByEmployee } from '../../../graphql/company/hooks/useUserManagerRolesByCompany'
import { refreshEmployeeSkillStatus } from '../../../graphql/skill-status/useSkillStatusMutations'
import { SkillStatusLabelComponent, SkillStatusDescription } from '../../skillStatus/SkillStatusLabelComponent'
import { GovernmentJobTitleHistoryTable } from '../contract/GovernmentJobTitleHistoryTable'
import { KudosCarousel } from '../../../components/custom_components/Carousel/KudosCarousel'
import {
  useAttentionPointsForEmployeeInPublicProfile,
  useKudosForEmployeeInPublicProfile,
} from '../../../graphql/kudos/useKudosForEmployeeWithCache'
import useIsFeatureEnabled from '../../../graphql/featureToggle/useIsFeatureEnabled'
import { FeatureNameEnum } from '../../../graphql/featureToggle/FeatureNameEnum'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'
import CreateKudosModalInPublicProfile from '../../../components/custom_components/CreateKudosModal/CreateKudosModalInPublicProfile'
import AttentionPointCarousel from '../../../components/custom_components/Carousel/AttentionPointCarousel'
import CreateAttentionPointModalInPublicProfile from '../../../components/custom_components/CreateAttentionpointModal/CreateAttentionPointModalInPublicProfile'
import EmployeeKudosOptOutModal from '../../../components/custom_components/KudosOptOutModal/EmployeeKudosOptOutModal'
import { NewLoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import { isHRAdminOfThisCountry } from '../../user/util'
import { EmployeeActivityListComponent } from '../../badge/EmployeeActivityListComponent'
interface PublicOverviewTabProps {
  profile: PublicProfileFragment
}

export const PublicOverviewTab: FC<PublicOverviewTabProps> = ({ profile }) => {
  const { t } = useTranslation()
  const { isFeatureEnabled: isKudosEnvVarEnabled } = useIsFeatureEnabled(FeatureNameEnum.KUDOS)
  const thisYear = new Date().getFullYear()
  const [isRefreshSkillStatusProcessing, toggleRefreshSkillStatusProcessing] = useState<boolean>(false)
  const [isUpdateOptOutModalVisible, setIsUpdateOptOutModalVisible] = useState<boolean>(false)
  const [newOptOutValue, setNewOptOutValue] = useState<boolean | undefined>(undefined)

  const [skillStatusError, setSkillStatusError] = useState<boolean>(false)
  const [isCreateKudosModalVisible, setIsCreateKudosModalVisible] = useState<boolean>(false)
  const [isCreateAttentionPointModalVisible, setIsCreateAttentionPointModalVisible] = useState<boolean>(false)

  const {
    isKudosEnabled,
    employeeOptedOut,
    currentKudos,
    currentPage,
    totalPages,
    loading: kudosLoading,
    error: kudosError,
    goToNextPage,
    goToPreviousPage,
    hasNextPage,
    hasPreviousPage,
    onCreateKudos,
    onKudosOptOutValueUpdated,
  } = useKudosForEmployeeInPublicProfile(profile.employeeId)

  const {
    data: contractData,
    error: contractError,
    loading: contractLoading,
  } = userCurrentContractWithCareerPathEvaluationInProfile(profile.employeeId, thisYear, true, profile.country)
  const { data, loading, error } = useTagHistoryOfEmployeeUntilReplyEntryDate(profile.employeeId)

  const { data: userRolesData } = useUserManagersRolesByEmployee(profile.employeeId)
  const userRoles: RoleName[] =
    userRolesData && userRolesData.findUserManagerRolesByEmployee ? userRolesData.findUserManagerRolesByEmployee : []
  const isBuOrCompanyManager = userRoles.includes(RoleName.BuManager) || userRoles.includes(RoleName.CompanyManager)

  const { user } = useAuth()
  const isUserHr = !!user.roles && user.roles.includes(RoleName.Hr)
  const isUserHrAdmin = isHRAdminOfThisCountry(profile.country, !!user.roles ? user.roles : [])

  const {
    currentAttentionPoints,
    currentPage: currentPageAP,
    totalPages: totalPagesAP,
    loading: loadingAP,
    error: errorAP,
    goToNextPage: goToNextPageAP,
    goToPreviousPage: goToPreviousPageAP,
    hasNextPage: hasNextPageAP,
    hasPreviousPage: hasPreviousPageAP,
    onCreateAttentionPoint,
  } = useAttentionPointsForEmployeeInPublicProfile(profile.employeeId, isUserHr)

  const timezone = {
    locale: user.locale,
    timezone: profile.employeeTimezone,
  }

  if (contractLoading || contractError || !contractData || loading || error || !data || !data.tagHistoryOfEmployeeUntilReplyEntryDate) {
    return (
      <NewLoadingAndErrors
        isLoading={contractLoading || loading}
        hasNoData={!contractData || !data || !data.tagHistoryOfEmployeeUntilReplyEntryDate}
        error={contractError || error}
        t={t}
        showSpinner
      />
    )
  }

  const currentContract: LatestContractDtoFragment = contractData.currentContract

  return (
    <>
      <PerFlexRow>
        <Col span={12}>
          <EmployeeInformationComponent
            profile={profile}
            currentContract={currentContract}
            publicProfile={true}
            tagHistory={data.tagHistoryOfEmployeeUntilReplyEntryDate}
            timezone={timezone}
          />
        </Col>
        <Col span={12}>
          <ContractComponent
            currentContract={currentContract}
            businessUnit={profile.businessUnit ? profile.businessUnit : undefined}
            isPublic
            careerPathEvaluation={contractData.careerPathEvaluation ? contractData.careerPathEvaluation : undefined}
            careerPath={contractData.careerPath ? contractData.careerPath : undefined}
            timezone={timezone}
            branchOfficeCode={profile.branchOfficeCode}
          />
        </Col>
      </PerFlexRow>

      <PerText appearance="tile-heading" mb="s">
        {t('profile.skillCenter.public.header')}
      </PerText>

      <Card bordered={false}>
        <PerFlexRow align="middle">
          {isBuOrCompanyManager && (
            <PerText mb="xxs">
              <Trans
                i18nKey="appraisalMeeting.stepOverview.skillCenterManager.desc"
                values={{
                  employee: getEmployeeFirstName({
                    legalFirstName: profile.legalFirstName,
                    legalLastName: profile.legalLastName,
                    chosenFirstName: profile.chosenFirstName,
                    chosenLastName: profile.chosenLastName,
                  } as Employee),
                }}
                components={[
                  <a
                    style={{ textDecoration: 'underline', textTransform: 'uppercase', fontWeight: 'bold' }}
                    href={`${profile.skillCenterUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {''}
                  </a>,
                ]}
              />
            </PerText>
          )}
        </PerFlexRow>
        <SkillStatusLabelComponent
          skillStatus={profile.skillStatus}
          skillStatusErrorVisible={skillStatusError}
          isProcessing={isRefreshSkillStatusProcessing}
        />
      </Card>

      <SkillStatusDescription
        skillStatus={profile.skillStatus}
        skillStatusErrorVisible={skillStatusError}
        onClick={async () => {
          toggleRefreshSkillStatusProcessing(true)
          const { data: skillStatusData, errors: skillStatusErrors } = await refreshEmployeeSkillStatus(profile.employeeId)
          if (!skillStatusData && skillStatusErrors) {
            setSkillStatusError(true)
          }
          toggleRefreshSkillStatusProcessing(false)
        }}
        isProcessing={isRefreshSkillStatusProcessing}
      />

      {profile.activityListEnabled && (
        <>
          <Divider />

          <EmployeeActivityListComponent employeeId={profile.employeeId} title={t('profile.activityList.publicHeader')} />
        </>
      )}

      {isKudosEnvVarEnabled && (
        <>
          {isKudosEnabled && (
            <>
              <EmployeeKudosOptOutModal
                visible={isUpdateOptOutModalVisible && newOptOutValue !== undefined}
                closeModal={() => {
                  setIsUpdateOptOutModalVisible(false)
                  setNewOptOutValue(undefined)
                }}
                newOptOutValue={newOptOutValue!}
                onKudosOptOutValueUpdated={onKudosOptOutValueUpdated}
                employeeId={profile.employeeId}
                employeeFullName={computeFullname(user.nameDisplayPreference, profile.legalFirstName, profile.legalLastName)}
              />
              <CreateKudosModalInPublicProfile
                visible={isCreateKudosModalVisible}
                closeModal={() => setIsCreateKudosModalVisible(false)}
                employeeId={profile.employeeId}
                employeeFullName={computeFullname(user.nameDisplayPreference, profile.legalFirstName, profile.legalLastName)}
                onKudosCreated={onCreateKudos}
              />
              <Divider />
              <PerFlexRow justify="space-between" align="middle" mb="xs">
                <PerText appearance="tile-heading">{t('kudos.header')}</PerText>
                <PerFlexRow>
                  {!employeeOptedOut && (
                    <PerButtonCircle
                      color="primary"
                      icon="add_stroke"
                      variant="pastel"
                      onClick={() => setIsCreateKudosModalVisible(true)}
                      tooltip={t('kudos.createModal.title')}
                      mr={isUserHrAdmin ? 'xs' : undefined}
                    />
                  )}
                  {isUserHrAdmin && (
                    <PerFlexRow align="middle" direction="column" justify="center">
                      <Tooltip
                        title={
                          employeeOptedOut
                            ? t('kudos.profileSection.optOut.tooltipButton.enable')
                            : t('kudos.profileSection.optOut.tooltipButton.disable')
                        }
                      >
                        <Switch
                          checked={!employeeOptedOut}
                          onChange={() => {
                            setNewOptOutValue(!employeeOptedOut)
                            setIsUpdateOptOutModalVisible(true)
                          }}
                        />
                      </Tooltip>
                    </PerFlexRow>
                  )}
                </PerFlexRow>
              </PerFlexRow>
              <KudosCarousel
                privateProfileView={false}
                employeeOptedOut={employeeOptedOut}
                kudos={currentKudos}
                nameDisplayPreference={user.nameDisplayPreference}
                loading={kudosLoading}
                error={kudosError || null}
                currentPage={currentPage}
                totalPages={totalPages}
                onNextPage={goToNextPage}
                onPreviousPage={goToPreviousPage}
                hasNextPage={hasNextPage}
                hasPreviousPage={hasPreviousPage}
              />
            </>
          )}

          {isUserHr && (
            <>
              <CreateAttentionPointModalInPublicProfile
                visible={isCreateAttentionPointModalVisible}
                closeModal={() => setIsCreateAttentionPointModalVisible(false)}
                employeeId={profile.employeeId}
                employeeFullName={computeFullname(user.nameDisplayPreference, profile.legalFirstName, profile.legalLastName)}
                onAttentionPointCreated={onCreateAttentionPoint}
              />
              <Divider />
              <PerFlexRow justify="space-between" align="middle" mb="xs">
                <PerText appearance="tile-heading">{t('attentionPoint.header')}</PerText>
                <PerButtonCircle
                  color="primary"
                  icon="add_stroke"
                  variant="pastel"
                  onClick={() => setIsCreateAttentionPointModalVisible(true)}
                  tooltip={t('attentionPoint.createModal.title')}
                />
              </PerFlexRow>
              <AttentionPointCarousel
                attentionPoints={currentAttentionPoints}
                nameDisplayPreference={user.nameDisplayPreference}
                loading={loadingAP}
                error={errorAP || null}
                currentPage={currentPageAP}
                totalPages={totalPagesAP}
                onNextPage={goToNextPageAP}
                onPreviousPage={goToPreviousPageAP}
                hasNextPage={hasNextPageAP}
                hasPreviousPage={hasPreviousPageAP}
              />
            </>
          )}
        </>
      )}

      <SalaryHistoryTable
        employeeId={profile.employeeId}
        isMonthlySalaryDisplayed={currentContract.isMonthlySalaryDisplayed}
        monthlySalaryMonthsInYear={currentContract.monthlySalaryMonthsInYear}
        timezone={timezone}
      />

      <BonusHistoryTable employeeId={profile.employeeId} timezone={timezone} />

      <NationalContractLevelHistoryTable employeeId={profile.employeeId} timezone={timezone} />

      <GovernmentJobTitleHistoryTable employeeId={profile.employeeId} timezone={timezone} />

      <LevelHistoryTable employeeId={profile.employeeId} timezone={timezone} />

      <PaymentHistoryTable employeeId={profile.employeeId} timezone={timezone} />

      {profile.education && profile.education.length > 0 && (
        <>
          <Divider />
          <EducationTable education={profile.education} timezone={timezone} />
        </>
      )}
    </>
  )
}
